<template>
  <sp-card v-if="i18nReady" class="sp-review-summary" variant="outlined">
    <sp-card-content slot="title" class="sp-review-summary__header">
      <sp-container
        flex
        direction="row"
        direction-sm="row"
        align-items="center"
        columngap="var(--sp-ref-scale-12)"
        rowgap="var(--sp-ref-scale-12)"
      >
        <sp-icon
          name="ai-sparkle"
          size="small"
          fill-color="var(--review-summary-icon-color)"
          aria-hidden="true"
        ></sp-icon>
        <sp-text class="sp-review-summary__title" tag="h3" type="h4" no-small-screen-type>{{ t("title") }}</sp-text>
        <sp-chip class="sp-review-summary__chip" variant="tonal" color="primary" size="small" pill>{{
          t("ai_generated")
        }}</sp-chip>
      </sp-container>
    </sp-card-content>
    <sp-card-content class="sp-review-summary__content">
      <sp-expandable-area
        v-if="hasSummary"
        class="sp-review-summary__body"
        activator-position="end"
        animated
        :title="t('read_full_summary')"
        :expanded-title="t('hide_full_summary')"
      >
        <sp-container
          slot="collapsed"
          class="sp-review-summary__text"
          flex
          direction="column"
          rowgap="var(--sp-ref-scale-12)"
        >
          <slot name="summary" />
        </sp-container>
        <sp-container class="sp-review-summary__text" flex direction="column" rowgap="var(--sp-ref-scale-12)">
          <slot />
        </sp-container>
      </sp-expandable-area>
      <sp-container v-else flex direction="column" rowgap="var(--sp-ref-scale-12)"><slot /></sp-container>
    </sp-card-content>
  </sp-card>
  <slot v-else />
</template>

<script setup>
import { nextTick, shallowRef, useHost } from "vue";
import { holdMountReady } from "#ce/framework/mount-ready";
import { t, whenI18nReady } from "./i18n.js";

const host = useHost();
const i18nReady = shallowRef(false);
// Read once at setup: the server renders the summary together with the card.
const hasSummary = host.querySelector(':scope > [slot="summary"]') !== null;

async function showTranslatedCard() {
  i18nReady.value = true;
  await nextTick();
}

holdMountReady(host, whenI18nReady.then(showTranslatedCard));
</script>

<style lang="scss">
@use "@assets/mixins/breakpoint";

:host {
  --review-summary-padding: var(--sp-comp-review-summary-padding, var(--sp-ref-scale-24));
  --review-summary-icon-color: var(--sp-comp-review-summary-icon-color, var(--sp-sys-color-primary));
  --sp-comp-text-color: var(--sp-comp-review-summary-text-color, var(--sp-sys-color-primary));

  display: block;
  margin-block: var(--sp-ref-scale-16) var(--sp-ref-scale-32);

  @include breakpoint.sp-breakpoint(xs) {
    --review-summary-padding: var(--sp-comp-review-summary-padding-small, var(--sp-ref-scale-16));
  }
}
</style>

<style lang="scss" scoped>
.sp-review-summary {
  --sp-comp-card-border-radius: var(--sp-comp-review-summary-border-radius, var(--sp-ref-border-radius-lg));
  --sp-comp-card-padding-inline: var(--review-summary-padding);

  &__header {
    --sp-comp-card-padding-block: var(--review-summary-padding) var(--sp-ref-scale-16);
  }

  &__title {
    --sp-comp-text-color: var(--sp-comp-review-summary-title-color, var(--sp-sys-color-primary));
  }

  &__content {
    --sp-comp-card-padding-block: 0 var(--review-summary-padding);
  }

  &__chip {
    --sp-comp-chip-border-width: 0;
    --sp-comp-chip-font-weight: var(--sp-sys-font-weight-normal);
  }

  &__text {
    padding-bottom: var(--sp-ref-scale-12);
  }

  &__body:not([data-sp-ce-ready]) > [slot="collapsed"] {
    display: none;
  }

  &__body {
    --sp-comp-expandable-area-activator-margin-bottom: 0;
    --sp-comp-expandable-area-caret-size: var(--sp-sys-icon-size-xxx-small);
    --sp-comp-expandable-area-activator-font-short: var(--sp-sys-typescale-small-bold-short);
    --sp-comp-expandable-area-activator-text-decoration: var(--sp-sys-typescale-link-text-decoration);
    --sp-comp-expandable-area-activator-gap: var(--sp-ref-scale-4);
  }
}
</style>
